import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar, 
  Chip, 
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider
} from '@mui/material';
import {
  SupervisorAccount as ProjectManagerIcon,
  PersonAdd as TeamMemberIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/api';

const KpikriTab = () => {
  const { user, getAuthHeaders } = useAuth();
  const [hierarchyData, setHierarchyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [formData, setFormData] = useState({
    input1: '',
    input2: '',
    input3: ''
  });
  const [viewFormData, setViewFormData] = useState({
    input1: '',
    input2: '',
    input3: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchUserHierarchy();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchUserHierarchy = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/user-hierarchy/user-hierarchy`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.data) {
        const data = response.data.data;
        setHierarchyData(data);
        
        // Set default selected member to current user
        if (data.user) {
          const currentUserId = data.user._id || data.user.id;
          setSelectedMember(currentUserId);
          
          // Fetch current user's form data
          try {
            const formResponse = await axios.get(
              `${API_URL}/kpikri/user/${currentUserId}`,
              { headers: getAuthHeaders() }
            );
            
            if (formResponse.data && formResponse.data.success && formResponse.data.form) {
              const form = formResponse.data.form;
              setFormData({
                input1: form.kpiMetric1,
                input2: form.kpiMetric2,
                input3: form.kriAssessment
              });
            }
          } catch (error) {
            console.error('Error fetching current user form:', error);
            // Don't show error for this as it's not critical
          }
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('Error fetching user hierarchy:', error);
      if (error.response) {
        setError(`Server error: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('No response from server. Please check your connection.');
      } else {
        setError('Failed to load hierarchy information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      const currentUserId = hierarchyData.user._id || hierarchyData.user.id;
      
      const response = await axios.post(
        `${API_URL}/kpikri/submit`,
        {
          userId: currentUserId,
          kpiMetric1: formData.input1,
          kpiMetric2: formData.input2,
          kriAssessment: formData.input3
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.success) {
        // Show success message
        alert('Form submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit form';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        KPI, KRI Form
      </Typography>

      {hierarchyData && hierarchyData.user && (
        <>
          {/* KPI/KRI Form */}
          <Card>
            <CardContent>
              <form onSubmit={handleFormSubmit}>
                {/* Form Inputs */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="KPI Metric 1"
                      value={formData.input1}
                      onChange={handleInputChange('input1')}
                      placeholder="Enter KPI metric 1"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="KPI Metric 2"
                      value={formData.input2}
                      onChange={handleInputChange('input2')}
                      placeholder="Enter KPI metric 2"
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="KRI Assessment"
                      value={formData.input3}
                      onChange={handleInputChange('input3')}
                      placeholder="Enter KRI assessment"
                      multiline
                      rows={4}
                      required
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    sx={{ minWidth: 120 }}
                  >
                    {submitting ? <CircularProgress size={24} /> : 'Fill Only Own'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>

          {/* View Forms Dropdown - Only for Project Managers */}
          {hierarchyData.role === 'project_manager' && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                  View Submitted Forms
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Form to View</InputLabel>
                  <Select
                    value={selectedMember || ''}
                    onChange={async (e) => {
                      const memberId = e.target.value;
                      if (memberId) {
                        try {
                          const response = await axios.get(
                            `${API_URL}/kpikri/user/${memberId}`,
                            { headers: getAuthHeaders() }
                          );
                          
                          if (response.data && response.data.success && response.data.form) {
                            const form = response.data.form;
                            setViewFormData({
                              input1: form.kpiMetric1,
                              input2: form.kpiMetric2,
                              input3: form.kriAssessment
                            });
                            setSelectedMember(memberId);
                          } else {
                            // If no form exists, clear the form
                            setViewFormData({ input1: '', input2: '', input3: '' });
                            setSelectedMember(memberId);
                          }
                        } catch (error) {
                          console.error('Error fetching form:', error);
                          // If error, clear the form
                          setViewFormData({ input1: '', input2: '', input3: '' });
                          setSelectedMember(memberId);
                        }
                      } else {
                        // If no member selected, clear the form
                        setViewFormData({ input1: '', input2: '', input3: '' });
                        setSelectedMember('');
                      }
                    }}
                    label="Select Form to View"
                  >
                    <MenuItem value="">
                      <em>Select a form to view</em>
                    </MenuItem>
                    {/* Current user */}
                    <MenuItem value={hierarchyData.user._id || hierarchyData.user.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 'small', color: 'primary.main' }} />
                        {hierarchyData.user.firstName} {hierarchyData.user.lastName} (Me)
                      </Box>
                    </MenuItem>
                    
                    {/* Team members */}
                    {hierarchyData.teamMembers && hierarchyData.teamMembers.map((member) => (
                      <MenuItem key={member._id} value={member._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TeamMemberIcon sx={{ fontSize: 'small', color: 'secondary.main' }} />
                          {member.firstName} {member.lastName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Display selected form data */}
                {selectedMember && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                      Selected Form Data:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>KPI 1:</strong> {viewFormData.input1 || 'No data'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>KPI 2:</strong> {viewFormData.input2 || 'No data'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>KRI:</strong> {viewFormData.input3 || 'No data'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Show error if hierarchyData exists but user is null */}
      {hierarchyData && !hierarchyData.user && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load user information. Please try refreshing the page.
        </Alert>
      )}
    </Box>
  );
};

export default KpikriTab; 